import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../api";
import { useT, useAffLangInit } from "../i18n";
import LangPicker from "../LangPicker";

export default function AffReset() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const t = useT();
  useAffLangInit();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError(t("reset.err.missing_token"));
      return;
    }
    if (password.length < 8) {
      setError(t("reset.err.too_short"));
      return;
    }
    if (password !== confirm) {
      setError(t("reset.err.mismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/affiliate/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/affiliate/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="dark fixed inset-0 flex items-center justify-center bg-black overflow-hidden"
      data-testid="aff-reset-screen"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-emerald-500/[0.06] blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/[0.05] blur-3xl" />
      </div>
      <form
        onSubmit={submit}
        className="relative z-10 w-full max-w-sm bg-black/60 border border-white/10 backdrop-blur-xl p-8 space-y-5"
        data-testid="aff-reset-form"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-3xl font-medium text-white tracking-tight">
              Clipra
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/40 mt-1">
              {t("reset.subtitle")}
            </div>
          </div>
          <LangPicker compact />
        </div>

        {done ? (
          <div className="space-y-3 text-sm text-white/70" data-testid="aff-reset-done">
            <p>{t("reset.done")}</p>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.18em] text-white/50 mb-1.5">
                {t("reset.new")}
              </label>
              <input
                type="password"
                autoFocus
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
                placeholder={t("signup.placeholder.password")}
                data-testid="aff-reset-password"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.18em] text-white/50 mb-1.5">
                {t("reset.confirm")}
              </label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
                placeholder={t("reset.placeholder.confirm")}
                data-testid="aff-reset-confirm"
              />
            </div>
            {error && (
              <div
                className="text-sm text-red-400 border-l-2 border-red-500 pl-3 py-1 font-mono"
                data-testid="aff-reset-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-white text-black py-2.5 text-sm font-medium tracking-wide hover:bg-white/90 disabled:opacity-50"
              data-testid="aff-reset-submit"
            >
              {busy ? t("reset.busy") : t("reset.cta")}
            </button>
            <div className="text-center text-xs font-mono text-white/40">
              <Link
                to="/affiliate/login"
                className="text-emerald-400 hover:text-emerald-300"
              >
                {t("common.back_to_signin")}
              </Link>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
